import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gauge, ChevronRight } from 'lucide-react';
import { useWallet } from '../../contexts/WalletContext';
import UsageBar from '../wallet/UsageBar';
import StorageBar from '../wallet/StorageBar';
import { getStorageStatus, type StorageStatus } from '../../services/walletService';
import { TYPE } from '../../constants/typeScale';

/** Plan name as a creator reads it — never the internal tier key. */
const PLAN_LABEL: Record<string, string> = { free: 'Creator', pro: 'Wisetree Plus', enterprise: 'Wisetree Studio' };

/**
 * USAGE — the one place a creator checks how much of their allowance is left (owner 2026-08-22:
 * "just have a bar on user's profile under usage section, similar to claude ai"). A quiet bar, a
 * plain-language line, and where the next refresh comes from. Deliberately no per-action prices.
 *
 * RF3-QUIET (owner 2026-08-23: "it is too much stress to show balance, unless user wants to check from
 * profile -> settings -> usage, like claude ai") made this the ONLY balance readout in the product — the
 * rail's credit + storage rings are gone. Storage joined it here, in plain megabytes.
 */
const UsageSection: React.FC = () => {
  const { wp, cap, neverFunded, plan, renewsAt, loading } = useWallet();
  const renewal = plan !== 'free' && renewsAt ? new Date(renewsAt) : null;
  // Storage moved here when the rail's meters were retired (RF3-QUIET) — it belongs with the other
  // "where do I stand" figure, not in the chrome. A failed read simply hides the bar; the quota is
  // enforced server-side either way, so a broken readout must never block the page.
  const [storage, setStorage] = useState<StorageStatus | null>(null);
  const [storageLoading, setStorageLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    getStorageStatus()
      .then((s) => { if (alive) setStorage(s); })
      .catch(() => { if (alive) setStorage(null); })
      .finally(() => { if (alive) setStorageLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6">
      <div className="flex items-center gap-2">
        <Gauge className="w-5 h-5 text-secondary-700 dark:text-secondary-200" />
        <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">Usage</h3>
        <span className="ml-auto text-xs text-secondary-500 dark:text-secondary-400">{PLAN_LABEL[plan] || 'Creator'} plan</span>
      </div>

      <div className="mt-4">
        <UsageBar remaining={wp} cap={cap} loading={loading} neverFunded={neverFunded} />
      </div>

      <p className="mt-3 text-[11px] text-secondary-400">
        {plan === 'free'
          ? 'Credits cover everything your crew does — talking, planning, and making media. You are only charged when something is delivered.'
          : renewal
            ? `Your credits refresh on ${renewal.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}. You are only charged when something is delivered.`
            : 'Your credits refresh every month. You are only charged when something is delivered.'}
      </p>

      {(storageLoading || storage) && (
        <div className="mt-5 border-t border-secondary-100 pt-4 dark:border-secondary-700">
          <p className={`${TYPE.label} text-secondary-400`}>Storage</p>
          <div className="mt-2">
            <StorageBar
              usedMb={storage?.usedMb ?? 0}
              capMb={storage?.capMb ?? 0}
              loading={storageLoading}
              upgrade={
                <Link
                  to="/wallet"
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-secondary-700 hover:text-secondary-900 dark:text-secondary-200 dark:hover:text-white"
                >
                  Get more storage <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              }
            />
          </div>
        </div>
      )}

      <Link
        to="/wallet"
        className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-secondary-700 hover:text-secondary-900 dark:text-secondary-200 dark:hover:text-white"
      >
        Manage plan and top up <ChevronRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
};

export default UsageSection;
